'use client';

import {
  ClickAwayListener,
  Container,
  LinkProps,
  SxProps,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  useMediaQuery,
} from '@mui/material';
import React, { ReactElement, ReactNode, useRef, useState } from 'react';
import { theme } from '../NerdWalletThemeProvider';

export interface IDisclosureTooltip extends LinkProps {
  children: ReactNode;
  trigger: ReactElement<{ onClick?: () => void }> | ((toggle: () => void) => ReactElement);
  anchorEl?: HTMLElement | null;
  onOpen?: (open: boolean) => void;
  keepMounted?: boolean;
  allowHoverInteraction?: boolean;
  placement?: TooltipProps['placement'];
  tooltipStyleOverride?: SxProps;
  zIndex?: number;
  popperMarginTop?: string | number;
}

const tooltipStyle = {
  border: `1px solid`,
  borderColor: 'neutral.light',
  backgroundColor: 'common.white',
};

export const DisclosureTooltip = ({
  trigger,
  children,
  anchorEl,
  onOpen,
  keepMounted = false,
  allowHoverInteraction = false,
  placement,
  zIndex,
  popperMarginTop = 3,
  tooltipStyleOverride,
}: IDisclosureTooltip) => {
  const [open, setOpen] = useState(false);
  const [keepOpen, setKeepOpen] = useState(false);
  const isSmAbove = useMediaQuery(theme.breakpoints.up('sm'));
  const containerRef = useRef<HTMLDivElement>(null);
  const additionalPopperProps: Record<string, HTMLElement> = {};

  const isTouchDevice = typeof window !== 'undefined' && 'ontouchstart' in window;
  const isHoverSupported = !isTouchDevice && allowHoverInteraction;

  if (anchorEl) {
    additionalPopperProps.anchorEl = anchorEl;
  }

  const toggleOpen = () => {
    const isOpen = !open;
    setOpen(isOpen);
    onOpen?.(isOpen);
  };

  const handleClose = () => {
    setOpen(false);
    onOpen?.(false);
    setKeepOpen(false);
  };

  return (
    <ClickAwayListener mouseEvent="onMouseDown" onClickAway={handleClose}>
      <span
        onClick={e => e.stopPropagation()}
        onMouseEnter={() => {
          if (allowHoverInteraction) {
            toggleOpen();
          }
        }}
        onMouseLeave={() => {
          if (allowHoverInteraction && !keepOpen) {
            handleClose();
          }
        }}
      >
        <Tooltip
          arrow
          open={open}
          placement={placement}
          onOpen={allowHoverInteraction ? toggleOpen : undefined}
          onClose={() => {
            if (!keepOpen) {
              handleClose();
            }
          }}
          slotProps={{
            ...(zIndex && { popper: { sx: { zIndex } } }),
            tooltip: {
              sx: {
                ...tooltipStyle,
                maxWidth: '340px',
                borderRadius: 0,
                textAlign: 'left',
                color: 'text.primary',
                fontWeight: 'normal',
                lineHeight: 1.8,
                boxShadow: 10,
                paddingX: 2,
                paddingY: 1.5,
                ...tooltipStyleOverride,
              },
            },
            arrow: {
              sx: {
                fontSize: '22px',
                '&::before': { ...tooltipStyle },
              },
            },
            popper: {
              keepMounted,
              ...additionalPopperProps,
              sx: {
                [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                  {
                    marginTop: popperMarginTop,
                  },
                [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]:
                  {
                    marginBottom: 3,
                  },
              },
              modifiers: [
                {
                  ...(isSmAbove && {
                    name: 'preventOverflow',
                    options: {
                      padding: { left: 20 },
                      boundary: 'window',
                    },
                  }),
                },
                {
                  name: 'offset',
                  options: {
                    offset: [0, -5],
                  },
                },
              ],
            },
          }}
          title={
            <Container ref={containerRef} disableGutters>
              {children}
            </Container>
          }
          disableFocusListener
          disableTouchListener
          disableHoverListener={!allowHoverInteraction}
        >
          {typeof trigger === 'function'
            ? trigger(() => {
                setKeepOpen(true);
                toggleOpen();
              })
            : React.cloneElement(trigger, {
                onClick: () => {
                  if (!isHoverSupported) {
                    setKeepOpen(true);
                    toggleOpen();
                  }
                  trigger.props?.onClick?.();
                },
              })}
        </Tooltip>
      </span>
    </ClickAwayListener>
  );
};
